import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CVProvider } from './contexts/CVContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './layouts/DashboardLayout';
import LoadingSpinner from './components/LoadingSpinner';

// Eagerly loaded pages (small, frequently accessed)
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ComingSoon from './pages/ComingSoon';
import ForgotPassword from './pages/ForgotPassword';
import Success from './pages/Success';
import Failed from './pages/Failed';
import VerifyEmail from './pages/VerifyEmail';

// Lazy loaded pages (large components, loaded on demand)
const Overview = lazy(() => import('./pages/Overview'));
const CVBuilder = lazy(() => import('./pages/CVBuilder'));
const EditResume = lazy(() => import('./pages/cv-builder/EditResume'));
const CVGeneration = lazy(() => import('./pages/CVGeneration'));
const JobSearch = lazy(() => import('./pages/JobSearch'));
const RemoteJobs = lazy(() => import('./pages/RemoteJobs'));
const JobLibrary = lazy(() => import('./pages/JobLibrary'));
const JobMatch = lazy(() => import('./pages/JobMatch'));
const AdvancedSearch = lazy(() => import('./pages/AdvancedSearch'));
const CoverLetterHistory = lazy(() => import('./pages/CoverLetterHistory'));
const AgentChat = lazy(() => import('./pages/AgentChat'));
const Subscription = lazy(() => import('./pages/Subscription'));
const Settings = lazy(() => import('./pages/Settings'));
const InterviewSimulator = lazy(() => import('./pages/InterviewSimulator'));
const CoverLetterGenerator = lazy(() => import('./pages/CoverLetterGenerator'));
const CVTranslator = lazy(() => import('./pages/CVTranslator'));
const PortfolioBuilder = lazy(() => import('./pages/PortfolioBuilder'));
const PortfolioView = lazy(() => import('./pages/PortfolioView'));
const ATSKeywordMatcher = lazy(() => import('./pages/ATSKeywordMatcher'));
const SkillGapAnalysis = lazy(() => import('./pages/SkillGapAnalysis'));
const LinkedInOptimizer = lazy(() => import('./pages/LinkedInOptimizer'));
const SalaryPredictor = lazy(() => import('./pages/SalaryPredictor'));
const ProjectGenerator = lazy(() => import('./pages/ProjectGenerator'));
const CulturalFitAnalyzer = lazy(() => import('./pages/CulturalFitAnalyzer'));

import { ErrorBoundary } from 'react-error-boundary';

function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-900 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-slate-100 text-center">
        <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
        <p className="text-sm text-slate-500 mb-6 font-mono text-left bg-slate-50 p-3 rounded-lg overflow-auto max-h-32">
          {error.message}
        </p>
        <button
          onClick={resetErrorBoundary}
          className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <CVProvider>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <AppRoutes />
            </ErrorBoundary>
          </CVProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Suspense wrapper for lazy-loaded components
const SuspenseWrapper = ({ children }) => (
  <Suspense fallback={
    <div className="flex items-center justify-center min-h-[60vh]">
      <LoadingSpinner size="lg" logoSpinner={true} />
    </div>
  }>
    {children}
  </Suspense>
);

function AppRoutes() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <main>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/success" element={<Success />} />
          <Route path="/failed" element={<Failed />} />
          <Route path="/p/:resumeId" element={<SuspenseWrapper><PortfolioView /></SuspenseWrapper>} />

          {/* Auth Routes - redirect to dashboard if already logged in */}
          <Route path="/login" element={
            <ProtectedRoute requireAuth={false} redirectTo="/dashboard">
              <Login />
            </ProtectedRoute>
          } />
          <Route path="/register" element={
            <ProtectedRoute requireAuth={false} redirectTo="/dashboard">
              <Register />
            </ProtectedRoute>
          } />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />



          {/* Protected Routes with Dashboard Layout */}
          <Route
            element={
              <ProtectedRoute requireAuth={true}>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<SuspenseWrapper><Overview /></SuspenseWrapper>} />
            <Route path="/cv-builder" element={<SuspenseWrapper><CVBuilder /></SuspenseWrapper>} />
            <Route path="/cv-builder/edit/:resumeId" element={<SuspenseWrapper><EditResume /></SuspenseWrapper>} />
            <Route path="/cv-generation/:resumeId" element={<SuspenseWrapper><CVGeneration /></SuspenseWrapper>} />
            <Route path="/job-search" element={<SuspenseWrapper><JobSearch /></SuspenseWrapper>} />
            <Route path="/job-match" element={<SuspenseWrapper><ComingSoon titleEn="Job Match AI" titleAr="مطابقة الوظائف" titleTr="İş Eşleştirme Yapay Zekası" /></SuspenseWrapper>} />
            <Route path="/remote-jobs" element={<SuspenseWrapper><RemoteJobs /></SuspenseWrapper>} />
            <Route path="/job-library" element={<SuspenseWrapper><JobLibrary /></SuspenseWrapper>} />
            <Route path="/advanced-search" element={<SuspenseWrapper><AdvancedSearch /></SuspenseWrapper>} />
            <Route path="/cover-letters" element={<SuspenseWrapper><CoverLetterHistory /></SuspenseWrapper>} />
            <Route path="/chatbot" element={<SuspenseWrapper><AgentChat /></SuspenseWrapper>} />
            <Route path="/subscription" element={<SuspenseWrapper><Subscription /></SuspenseWrapper>} />
            <Route path="/settings" element={<SuspenseWrapper><Settings /></SuspenseWrapper>} />
            <Route path="/cover-letter-generator" element={<SuspenseWrapper><CoverLetterGenerator /></SuspenseWrapper>} />
            <Route path="/portfolio-builder" element={<SuspenseWrapper><PortfolioBuilder /></SuspenseWrapper>} />
            <Route path="/cv-translator" element={<SuspenseWrapper><ComingSoon titleEn="CV Translator" titleAr="مترجم السيرة الذاتية" titleTr="CV Çevirmen" /></SuspenseWrapper>} />
            <Route path="/ats-keyword-matcher" element={<SuspenseWrapper><ATSKeywordMatcher /></SuspenseWrapper>} />
            <Route path="/skill-gap-analysis" element={<SuspenseWrapper><ComingSoon titleEn="Skill Gap Analysis" titleAr="فجوة المهارات" titleTr="Beceri Boşluğu Analizi" /></SuspenseWrapper>} />
            <Route path="/linkedin-optimizer" element={<SuspenseWrapper><LinkedInOptimizer /></SuspenseWrapper>} />
            <Route path="/salary-predictor" element={<SuspenseWrapper><ComingSoon titleEn="Salary Predictor" titleAr="متوقع الرواتب" titleTr="Maaş Tahmin Edici" /></SuspenseWrapper>} />
            <Route path="/project-generator" element={<SuspenseWrapper><ComingSoon titleEn="Project Generator" titleAr="صانع المشاريع العملية" titleTr="Proje Oluşturucu" /></SuspenseWrapper>} />
            <Route path="/cultural-fit" element={<SuspenseWrapper><ComingSoon titleEn="Cultural Fit Analyzer" titleAr="محلل ثقافة الشركات" titleTr="Kültürel Uyum Analizörü" /></SuspenseWrapper>} />
            <Route path="/interview-prep" element={<SuspenseWrapper><InterviewSimulator /></SuspenseWrapper>} />
          </Route>
        </Routes>
      </main>
    </div>
  );
}

export default App; 